import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography, Stack, Chip } from '@mui/material';
import { DomainObjectModal } from '@platform/core/src/components/domain';
import { bioLogConfig } from '../../config/domainConfig';
import DynamicIcon from '../common/DynamicIcon';
import { getTrackableDefinitions } from '../../services/trackablesApi';

const BioLogReferenceViewer = ({ resource, onActionSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [definition, setDefinition] = useState(null);

  useEffect(() => {
    if (resource?.log_type) {
      getTrackableDefinitions().then(defs => {
        const match = defs.find(d => d.key === resource.log_type);
        if (match) setDefinition(match);
      });
    }
  }, [resource?.log_type]);

  if (!resource) {
    return <Typography variant="body2" color="text.secondary">Loading log...</Typography>;
  }

  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);

  const handleModalActionSuccess = (action, item) => {
    if (action === 'delete') {
      handleClose();
      if (onActionSuccess) {
        onActionSuccess(action, item);
      }
    }
  };

  const logType = resource.log_type || 'WEIGHT';
  const iconName = definition?.icon || 'note';
  const displayName = definition?.display_name || logType.toLowerCase().replace(/_/g, ' ');
  
  // Generate a smart summary
  let summary = 'Log Entry';
  if (resource.data) {
      if (resource.data.value !== undefined && resource.data.value !== null) {
          summary = `${resource.data.value} ${resource.data.unit || ''}`;
      }
      else if (resource.data.hours) summary = `${resource.data.hours} hrs`;
      else if (resource.data.calories) summary = `${resource.data.calories} kcal`;
      else if (resource.data.feelings) summary = Array.isArray(resource.data.feelings) ? resource.data.feelings.join(', ') : resource.data.feelings;
      else {
        // Fallback for custom fields: grab the first non-unit field
        const keys = Object.keys(resource.data).filter(k => !k.endsWith('_unit') && k !== 'unit');
        if (keys.length > 0) {
            const firstKey = keys[0];
            const val = resource.data[firstKey];
            // Format boolean "true" to "Yes"
            const displayVal = val === true ? 'Yes' : (val === false ? 'No' : val);
            summary = `${firstKey.replace(/_/g, ' ')}: ${displayVal}`;
        }
      }
  }

  return (
    <>
      <Paper
        variant="outlined"
        onClick={handleOpen}
        sx={{
          p: 1.5,
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 1,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <DynamicIcon icon={iconName} sx={{ color: 'primary.main' }} />
          <Stack sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <Typography variant="body1" fontWeight="medium" noWrap sx={{ textTransform: 'capitalize' }}>
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary}
            </Typography>
          </Stack>
          <Chip label="Log" size="small" variant="outlined" />
        </Stack>
      </Paper>

      {isModalOpen && (
        <DomainObjectModal
          open={isModalOpen}
          onClose={handleClose}
          itemToEdit={resource}
          config={bioLogConfig}
          sectionTitle={`${displayName} Details`}
          onActionSuccess={handleModalActionSuccess}
        />
      )}
    </>
  );
};

BioLogReferenceViewer.propTypes = {
  resource: PropTypes.object,
  onActionSuccess: PropTypes.func,
};

export default BioLogReferenceViewer;